import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'
import {
  mintUploadTarget,
  uploadToStorage,
  useEvidenceUploadFlow,
  type Evidence,
  type UploadTarget,
} from '@/hooks/sgsst/evidencias/use-evidence'

export type Investigacion = components['schemas']['InvestigacionRead']
export type EquipoSeat = components['schemas']['EquipoSeatRead']
export type CatalogItem = components['schemas']['CatalogItem']
export type SeatPayload = components['schemas']['SeatPayload']
export type CausaPayload = components['schemas']['CausaPayload']
export type CausaPatch = components['schemas']['CausaPatch']
export type Causa = components['schemas']['CausaRead']
export type MedidaPayload = components['schemas']['MedidaPayload']
export type Medida = components['schemas']['MedidaRead']
export type ManualPayload = components['schemas']['ManualPayload']
export type InvestigacionPatch = components['schemas']['InvestigacionPatch']
export type EquipoRol = components['schemas']['EquipoRol']
export type CausaTipo = components['schemas']['CausaTipo']
export type Metodologia = components['schemas']['InvestigacionMetodologia']
export type CausaCategoria = components['schemas']['CausaCategoria']
export type CategoriaItem = components['schemas']['CategoriaItem']
export type Indicadores = components['schemas']['IndicadoresRead']
export type Indicador = components['schemas']['IndicadorItem']
export type IndicadoresSerie = components['schemas']['IndicadoresSerieRead']
export type EvidenceKind = components['schemas']['SgsstEvidenceKind']

function base(businessId: string, incidenteId: string): string {
  return `/businesses/${businessId}/incidentes/${incidenteId}/investigacion`
}

// Se crea en el primer acceso. `puedeOperar` apaga la consulta: es solo de operador y un 403
// no se distingue de «todavía no hay nada».
export function useInvestigacion(businessId: string | undefined, incidenteId: string | undefined, puedeOperar = true) {
  return useQuery({
    queryKey: ['investigacion', businessId, incidenteId],
    queryFn: async () => (await api.get<Investigacion>(base(businessId!, incidenteId!))).data,
    enabled: !!businessId && !!incidenteId && puedeOperar,
  })
}

// Catálogo de causas del art. 3; `categoria` filtra por espina de Ishikawa (6M SST).
export function useCausasCatalog(businessId: string | undefined, q: string, categoria?: CausaCategoria) {
  return useQuery({
    queryKey: ['catalogo-causas', businessId, q, categoria ?? 'all'],
    queryFn: async () =>
      (
        await api.get<CatalogItem[]>(`/businesses/${businessId}/catalogos/causas`, {
          params: {q: q || undefined, categoria: categoria || undefined},
        })
      ).data,
    enabled: !!businessId,
  })
}

export function useCategorias(businessId: string | undefined) {
  return useQuery({
    queryKey: ['catalogo-categorias', businessId],
    queryFn: async () => (await api.get<CategoriaItem[]>(`/businesses/${businessId}/catalogos/categorias`)).data,
    enabled: !!businessId,
  })
}

function useInvInvalidation(businessId: string, incidenteId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({queryKey: ['investigacion', businessId, incidenteId]})
    queryClient.invalidateQueries({queryKey: ['incidente', businessId, incidenteId]})
    queryClient.invalidateQueries({queryKey: ['incidentes', businessId]})
    queryClient.invalidateQueries({queryKey: ['actions', businessId]})
    queryClient.invalidateQueries({queryKey: ['indicadores', businessId]})
  }
}

function useInvAction<TBody>(businessId: string, incidenteId: string, path: string, method: 'post' | 'patch' = 'post') {
  const invalidate = useInvInvalidation(businessId, incidenteId)
  return useMutation({
    mutationFn: async (body?: TBody) => {
      const url = `${base(businessId, incidenteId)}${path}`
      const resp =
        method === 'patch'
          ? await api.patch<Investigacion>(url, body ?? {})
          : await api.post<Investigacion>(url, body ?? {})
      return resp.data
    },
    onSuccess: invalidate,
  })
}

export function usePatchInvestigacion(businessId: string, incidenteId: string) {
  return useInvAction<InvestigacionPatch>(businessId, incidenteId, '', 'patch')
}

export function usePrefillEquipo(businessId: string, incidenteId: string) {
  return useInvAction<void>(businessId, incidenteId, '/equipo/prefill')
}

export function useAddSeat(businessId: string, incidenteId: string) {
  return useInvAction<SeatPayload>(businessId, incidenteId, '/equipo')
}

export function useAddCausa(businessId: string, incidenteId: string) {
  return useInvAction<CausaPayload>(businessId, incidenteId, '/causas')
}

// No toca `parent_causa_id`: mover una causa de padre es borrarla y volverla a crear.
export function useEditCausa(businessId: string, incidenteId: string) {
  const invalidate = useInvInvalidation(businessId, incidenteId)
  return useMutation({
    mutationFn: async (input: {causaId: string; patch: CausaPatch}) =>
      (await api.patch<Investigacion>(`${base(businessId, incidenteId)}/causas/${input.causaId}`, input.patch)).data,
    onSuccess: invalidate,
  })
}

// Las causas derivadas no se borran: el backend las engancha a la superior de la eliminada.
export function useRemoveCausa(businessId: string, incidenteId: string) {
  const invalidate = useInvInvalidation(businessId, incidenteId)
  return useMutation({
    mutationFn: async (causaId: string) => api.delete(`${base(businessId, incidenteId)}/causas/${causaId}`),
    onSuccess: invalidate,
  })
}

export function useAddMedida(businessId: string, incidenteId: string) {
  return useInvAction<MedidaPayload>(businessId, incidenteId, '/medidas')
}

export function useCompletarInvestigacion(businessId: string, incidenteId: string) {
  return useInvAction<void>(businessId, incidenteId, '/completar')
}

export function useMarcarManual(businessId: string, incidenteId: string) {
  return useInvAction<ManualPayload>(businessId, incidenteId, '/manual')
}

export function informeUrl(businessId: string, incidenteId: string): string {
  return `${base(businessId, incidenteId)}/informe`
}

export type VentanaTendencia = 'semana' | 'mes'

export function useIndicadores(businessId: string | undefined, ventana: VentanaTendencia, year?: number) {
  return useQuery({
    queryKey: ['indicadores', businessId, year ?? 'current', ventana],
    queryFn: async () =>
      (await api.get<Indicadores>(`/businesses/${businessId}/indicadores`, {params: {year, ventana}})).data,
    enabled: !!businessId,
  })
}

export function useIndicadoresSerie(businessId: string | undefined, meses = 12) {
  return useQuery({
    queryKey: ['indicadores-serie', businessId, meses],
    queryFn: async () =>
      (await api.get<IndicadoresSerie>(`/businesses/${businessId}/indicadores/serie`, {params: {meses}})).data,
    enabled: !!businessId,
  })
}

export function useInvestigacionEvidencia(
  businessId: string | undefined,
  incidenteId: string | undefined,
  puedeOperar = true
) {
  return useQuery({
    queryKey: ['investigacion-evidencia', businessId, incidenteId],
    queryFn: async () => (await api.get<Evidence[]>(`${base(businessId!, incidenteId!)}/evidencia`)).data,
    enabled: !!businessId && !!incidenteId && puedeOperar,
  })
}

// `investigacionId` es la PK de la fila `sgsst_investigacion`; `incidenteId` solo arma la URL
// de la lista. No son intercambiables: el del incidente en el body da 404.
export function useRegisterInvestigacionEvidencia(businessId: string, incidenteId: string, investigacionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      target: UploadTarget
      filename: string
      content_type: string
      size_bytes: number
      kind: EvidenceKind
    }) =>
      api.post(`/businesses/${businessId}/sgsst/evidence`, {
        investigacion_id: investigacionId,
        object_key: input.target.object_key,
        filename: input.filename,
        content_type: input.content_type,
        size_bytes: input.size_bytes,
        kind: input.kind,
        validate_ai: false,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['investigacion-evidencia', businessId, incidenteId]})
    },
  })
}

/** Mint, PUT y registro en un solo lugar: el par de ids es fácil de confundir y no se copia. */
export function useSubirEvidenciaInvestigacion(
  businessId: string,
  incidenteId: string,
  investigacionId: string,
  kind: EvidenceKind
) {
  const register = useRegisterInvestigacionEvidencia(businessId, incidenteId, investigacionId)
  return useEvidenceUploadFlow(async (file, onProgress) => {
    const target = await mintUploadTarget(businessId, file)
    await uploadToStorage(target, file, onProgress)
    await register.mutateAsync({
      target,
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
      kind,
    })
  })
}
